import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Full-screen viewer for the finalized signed PDF document.
 * Uses a local blob URL to avoid Dropbox X-Frame-Options restrictions.
 */
const FinalDocumentViewer = ({ finalDocument, onClose, onSendToClient }) => {
  const { t } = useTranslation();
  const iframeRef = useRef(null);
  const [hashExpanded, setHashExpanded] = useState(false);

  const handleNativeFullscreen = useCallback(() => {
    const el = iframeRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  }, []);

  if (!finalDocument) return null;

  const { blobUrl, documentHash, fileName, version, createdAt } = finalDocument;

  const formattedDate = createdAt ? new Date(createdAt).toLocaleString() : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">

      {/* Top toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-800 border-b border-gray-700 shrink-0">

        {/* Shield + hash */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {documentHash && (
            <button
              onClick={() => setHashExpanded(p => !p)}
              className="flex items-center gap-1.5 text-green-400 hover:text-green-300 transition-colors shrink-0"
              title={hashExpanded ? 'Hide hash' : 'Show integrity hash'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-xs font-semibold">Verified</span>
            </button>
          )}

          {hashExpanded && documentHash && (
            <span className="text-xs font-mono text-green-300 truncate">
              {documentHash}
            </span>
          )}

          {!hashExpanded && (
            <div className="flex items-center gap-2 text-gray-300 min-w-0">
              <span className="text-sm font-medium truncate">
                {fileName || t('finalDocumentViewer.title', 'Final Signed Document')}
              </span>
              {version && (
                <span className="text-xs text-gray-500 shrink-0">v{version}</span>
              )}
              {formattedDate && (
                <span className="text-xs text-gray-500 shrink-0 hidden sm:block">{formattedDate}</span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Send to Client */}
          {onSendToClient && (
            <button
              onClick={onSendToClient}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors mr-1"
              title="Send to Client"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('finalDocumentViewer.sendToClient', 'Send to Client')}
            </button>
          )}

          {/* Fullscreen */}
          <button
            onClick={handleNativeFullscreen}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            title="Fullscreen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* PDF */}
      <div className="flex-1 overflow-hidden">
        {blobUrl ? (
          <iframe
            ref={iframeRef}
            src={blobUrl}
            title={fileName || 'Final Document'}
            className="w-full h-full border-0"
            allow="fullscreen"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            {t('finalDocumentViewer.noUrl', 'Document URL not available.')}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalDocumentViewer;
